// Service-site articles for club owners and racers, shown on the RC Race HQ
// front page (www / www-dev). Kept in code, not the Information table, so
// they ship with the frontend and land on prod with the dev -> main merge.
// Only describe what the platform does today; anything not built yet is
// labelled "coming soon".

export interface PlatformArticle {
  slug: string;
  title: string;
  summary: string;
  /** Paragraphs; a paragraph starting with "- " lines renders as a list. */
  body: string[];
}

export const PLATFORM_ARTICLES: PlatformArticle[] = [
  {
    slug: "race-day-for-rc-clubs",
    title: "Race day, without the clipboard",
    summary:
      "RC Race HQ puts your club's events, registration, heats and race-day media in one place, so your volunteers can run the track instead of the paperwork.",
    body: [
      "Most clubs run race day on a mix of a Facebook group, a spreadsheet, a cash box and whoever remembered the clipboard. RC Race HQ replaces that with one site built for how club racing actually works.",
      "- Post events with their classes, dates and entry fees\n- Racers register and pay online before they get to the track\n- Build each day's qualifying heats and mains, in run order\n- Open any race to see everyone registered in that class\n- Share photos and video from race day, grouped by event",
      "Your members get one account that works at every club on RC Race HQ. If they race with you on Saturday and travel to another club next month, their profile, vehicles and registrations come with them.",
    ],
  },
  {
    slug: "your-club-your-site",
    title: "Your club, on your own domain",
    summary:
      "Keep your club's name and web address. RC Race HQ serves your events, news and hobby shops on your domain, with your look.",
    body: [
      "Your club keeps its own identity. Point your domain (for example yourclub.com) at RC Race HQ and visitors see your club's site: your name in the header, your events, your news and your local hobby shops.",
      "Setup is a couple of DNS records. We provision the certificate for you, and the domain test in your club settings checks DNS, HTTPS and that the domain reaches your club, step by step, with a plain-English fix for anything that isn't right.",
      "Sign-in, checkout and account pages stay on rcracehq.com. That keeps payments and logins on one secure, trusted address for every racer, no matter which club's site they started on.",
    ],
  },
  {
    slug: "tools-for-club-managers",
    title: "Built for the people who run the club",
    summary:
      "Owners and managers get a Manage area for events, heats, news, hobby shops, live streams and credits, without needing admin access to the whole platform.",
    body: [
      "Every club has owners and managers. Owners can add and remove managers; managers handle the day-to-day.",
      "- Events: add race days and lay out qualifying heats and mains for each day\n- News: publish club news and choose the order it appears in\n- Hobby shops: list local shops with address, map, phone and hours\n- Streaming: link your LiveRC page and embed up to six video players on your live page\n- Credits: award race credits to members, kept separate for each club",
      "If you manage more than one club, you pick which one you're working on and everything stays scoped to it.",
    ],
  },
  {
    slug: "for-racers",
    title: "One account for every track",
    summary:
      "Racers sign up once, keep their cars and transponders in one garage, and see every race they're registered for across every club using RC Race HQ.",
    body: [
      "Your RC Race HQ account follows you from club to club.",
      "- Profile: your details, set once\n- Vehicles: your cars, with manufacturer, class and radio\n- Registrations: every race you're signed up for, at every club, in one list\n- Credits: race credits from each club, tracked separately",
      "Race-day text reminders are on the way. Add your phone number now and choose whether you'd like a heads-up when your race is coming up; you can change that at any time on your profile.",
    ],
  },
  {
    slug: "whats-next",
    title: "What's coming next",
    summary:
      "Lap timing, race-day notifications and per-club theming are in the works. Here's where RC Race HQ is headed.",
    body: [
      "RC Race HQ is growing, and the next pieces are aimed at race day itself.",
      "- Lap timing (coming soon): our own timing hardware and software, so results flow straight from the track into your events\n- Notifications (coming soon): a text or push alert when a racer's heat is coming up\n- Club theming (coming soon): your colors, logo and fonts, with advanced custom CSS for clubs that want full control",
      "Running a club and want to be one of the first on board? Get in touch and tell us how your race days run today.",
    ],
  },
];

export function findPlatformArticle(slug: string | undefined): PlatformArticle | undefined {
  return PLATFORM_ARTICLES.find((a) => a.slug === slug);
}
